import { PlayerHookType } from '@cbsinteractive/avia-js';
import { HookContextInterface } from '@cbsinteractive/avia-js/dist/types/iface/HookContextInterface';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/hooks/AbstractAviaPlayerHook';

/**
 * @copyright © Paramount Streaming
 * @author Patrick Porter
 * @description Premium Audio hook
 */
export class AviaPlayerPremiumAudioHook extends AbstractAviaPlayerHook {

	/**
	 * @description Premium Audio hook
	 */
	hookType: PlayerHookType = PlayerHookType.RESPONSE;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerPremiumAudioHook'
			}
		);

		// setup tag events handler
		this.tagEventsHandler( this.#onTagEvent, [
			TagEvent.ON_CREATE_PLAYER_SUCCESS
		] );
	}

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				// stop listening to this event... we need it once per instance to prevent memory leak
				this.removeTagEventsHandler(
					this.#onTagEvent,
					this.tag.eventDispatcher,
					[ TagEvent.ON_CREATE_PLAYER_SUCCESS ]
				).then(
					( data ) => {
						this.registerHook();
					}
				);
				break;
		}
	};

	/**
	 * @description Callback passed to player's registerHook function
	 * @param {HookContextInterface<any>} context
	 */
	callback = ( context: HookContextInterface<any> ): void => {
		const response = context.value;

		const isLive = this.tag?.params?.CONTENT?.apiMetadata?.[ 0 ]?.isLive || this.tag?.params?.CONTENT?.resource?.metadata?.isLive;
		if ( response?.url.includes( '.mpd' ) && !isLive ) {
			try {
				const xml = new DOMParser().parseFromString( response.data, 'text/xml' );
				this?.logger?.log( 'xml:', xml );

				const tracks = this.#pullAudioTracksFromManifest( xml );
				const ec3 = tracks?.ec3 || [];
				const aac = tracks?.aac || [];

				// if standard tracks array has a length of zero, abandon audio track processing
				if ( !aac.length ) {
					this?.logger?.warn( 'Possible issue with video manifest or loading of video manifest, length of standard audio tracks is Zero' );
					this?.logger?.log( 'response:', response );
					return;
				}

				if ( !this.#isPremiumAudioTracksIncomplete( aac, ec3 ) ) {
					// the two sets of audio tracks are complete
					this?.logger?.log( 'Available audio codecs match; DID NOT manipulate manifest' );
					return;
				}

				// remove ec3 tracks from manifest
				ec3.forEach( ( set ) => {
					set.parentNode.removeChild( set );
				} );

				const serializer = new XMLSerializer();
				const updatedMpd = serializer.serializeToString( xml );

				if ( updatedMpd ) {
					// set new manifest data
					context.value.data = updatedMpd;
					this?.logger?.log( 'Available audio codecs mismatched; removing ec3 from manifest' );
				} else {
					this?.logger?.warn( 'There was a problem when trying to remove ec3 tracks from manifest' );
				}

			} catch ( error ) {
				this?.logger?.warn( error );
			}
		}
	};

	#isPremiumAudioTracksIncomplete = ( standardTracks: Element[], premiumTracks: Element[] ): boolean => {
		// sanity check that we're able to iterate over each set of tracks
		if ( !this.utils.Type.isArray( standardTracks ) || !this.utils.Type.isArray( premiumTracks ) ) {
			return true;
		}

		this?.logger?.log( 'standardTracks length:', standardTracks.length );
		this?.logger?.log( 'premiumTracks length:', premiumTracks.length );

		// if the arrays are not the same length, return true
		if ( standardTracks.length !== premiumTracks.length ) {
			return true;
		}

		if ( standardTracks.length === premiumTracks.length ) {
			this?.logger?.log( 'list lengths are the same', standardTracks, premiumTracks );
		}

		let foundIncompleteTrack = false;

		// Check each track for incompleteness
		standardTracks.forEach( ( standardTrack ) => {
			if ( foundIncompleteTrack ) return;

			const standardRole = standardTrack.querySelector( 'Role' ) as Node;
			const standardLang = standardTrack.attributes?.getNamedItem( 'lang' )?.value?.toLowerCase?.();

			// dont bother trying to match tracks with missing languages (ad tracks)
			if ( !standardLang ) return;

			let matchingTrack = undefined;

			// Look through the other set of audio tracks to find a match
			premiumTracks.forEach( ( premiumTrack ) => {
				// Have we already found a match?
				if ( !this.utils.Type.isNullOrUndefined( matchingTrack ) ) return;

				const premiumRole = premiumTrack.querySelector( 'Role' ) as Node;
				const sameRole = ( !standardRole && !premiumRole ) || standardRole?.isEqualNode( premiumRole );

				const premiumLang = premiumTrack.attributes?.getNamedItem( 'lang' )?.value?.toLowerCase?.();
				const sameLanguage = standardLang === premiumLang;

				if ( sameRole && sameLanguage ) {
					matchingTrack = premiumTrack;
				}
			} );

			if ( this.utils.Type.isNullOrUndefined( matchingTrack ) ) {
				this?.logger?.log( 'no matching track found for', standardTrack, standardRole, standardLang );
				foundIncompleteTrack = true;
				return;
			}
		} );

		return foundIncompleteTrack;
	};

	#pullAudioTracksFromManifest = ( manifestXML: Document ): {ec3: Element[], aac: Element[]} => {
		const sets = manifestXML.querySelectorAll( 'AdaptationSet[contentType=audio]' );
		this?.logger?.log( 'sets:', sets );
		const ec3 = [], aac = [];

				// Get all the audio sets and split them into 2 separate lists based on codec
		for ( let i = 0; i < sets.length; i++ ) {
			( sets[ i ].querySelector( 'Representation[codecs=ec-3]' ) ) ? ec3.push( sets[ i ] ) : aac.push( sets[ i ] );
		}

		this?.logger?.log( 'audio tracks ec3: ', ec3 );
		this?.logger?.log( 'audio tracks aac: ', aac );

		return {
			ec3,
			aac
		};
	};
}